import { useState, useCallback } from 'react'
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Chip, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material'
import { Add, Delete, Edit, OpenInNew, Link } from '@mui/icons-material'
import { useApi } from '../../api/client'
import {
  fetchAllMashups, createSettingsMashup, updateSettingsMashup, deleteSettingsMashup,
  type SettingsMashupRow,
} from '../../api/mashups'
import { ConfirmDialog } from '../../components/shared/ConfirmDialog'
import { useToastStore } from '../../stores/toast.store'

interface ObjectItem {
  id: string
  name: string
  label: string
  is_system: number
}

const EMPTY_FORM = { object_id: '', title: '', url: '', sort_order: 0 }

export function MashupManagement() {
  const { data, mutate } = useApi<{ data: SettingsMashupRow[] }>('/settings/mashups')
  const { data: objectsData } = useApi<{ data: ObjectItem[] }>('/objects')
  const toast = useToastStore()

  const mashups = data?.data ?? []
  const objects = objectsData?.data ?? []

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<SettingsMashupRow | null>(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  const filteredMashups = mashups.sort((a, b) => {
    const labelCompare = a.object_label.localeCompare(b.object_label)
    if (labelCompare !== 0) return labelCompare
    return a.sort_order - b.sort_order
  })

  const openCreateDialog = useCallback(() => {
    setEditing(null)
    setForm(EMPTY_FORM)
    setDialogOpen(true)
  }, [])

  const openEditDialog = useCallback((mashup: SettingsMashupRow) => {
    setEditing(mashup)
    setForm({
      object_id: mashup.object_id,
      title: mashup.title,
      url: mashup.url,
      sort_order: mashup.sort_order,
    })
    setDialogOpen(true)
  }, [])

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      if (editing) {
        await updateSettingsMashup(editing.id, {
          object_id: form.object_id || undefined,
          title: form.title || undefined,
          url: form.url || undefined,
          sort_order: form.sort_order,
        })
      } else {
        await createSettingsMashup({
          object_id: form.object_id,
          title: form.title,
          url: form.url,
          sort_order: form.sort_order,
        })
      }
      mutate()
      setDialogOpen(false)
      toast.success(editing ? 'Mashup 已更新' : 'Mashup 已创建')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败')
    } finally {
      setSaving(false)
    }
  }, [editing, form, mutate])

  const handleDelete = useCallback(async (id: string) => {
    try {
      await deleteSettingsMashup(id)
      mutate()
      toast.success('Mashup 已删除')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '删除失败')
    }
    setDeleteConfirm(null)
  }, [mutate, toast])

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditing(null)
    setForm(EMPTY_FORM)
  }

  const isFormValid = form.object_id && form.title.trim() && form.url.trim()

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Mashups</Typography>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={openCreateDialog}>
          Add Mashup
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add external links as embedded panels (iframes) to any object&apos;s detail view.
        Supports both system and custom objects.
      </Typography>

      {filteredMashups.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Link sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">No mashups configured yet.</Typography>
          <Typography variant="body2" color="text.secondary">
            Click &quot;Add Mashup&quot; to embed an external link into an object&apos;s detail view.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Object</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>URL</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Sort Order</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMashups.map((mashup) => {
                const obj = objects.find((o) => o.id === mashup.object_id)
                return (
                  <TableRow key={mashup.id}>
                    <TableCell>
                      <Chip
                        label={obj?.label ?? mashup.object_label}
                        size="small"
                        variant={obj?.is_system ? 'filled' : 'outlined'}
                        color={obj?.is_system ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{mashup.title}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: 400 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {mashup.url}
                        </Typography>
                        <IconButton
                          size="small"
                          href={mashup.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <OpenInNew fontSize="inherit" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>{mashup.sort_order}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => openEditDialog(mashup)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => setDeleteConfirm(mashup.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Mashup' : 'Add Mashup'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Object</InputLabel>
            <Select
              value={form.object_id}
              onChange={(e) => setForm((p) => ({ ...p, object_id: e.target.value }))}
              label="Object"
            >
              {objects
                .sort((a, b) => {
                  if (a.is_system !== b.is_system) return b.is_system - a.is_system
                  return a.label.localeCompare(b.label)
                })
                .map((obj) => (
                  <MenuItem key={obj.id} value={obj.id}>
                    {obj.is_system ? '⭐ ' : ''}{obj.label}
                    {!obj.is_system && ' (Custom)'}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            label="Title"
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            fullWidth
            size="small"
            sx={{ mt: 2 }}
            placeholder="e.g. Market Report"
          />
          <TextField
            label="URL"
            value={form.url}
            onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))}
            fullWidth
            size="small"
            sx={{ mt: 2 }}
            placeholder="https://example.com/embed"
            helperText="This URL will be embedded as an iframe in the object's detail view"
          />
          <TextField
            label="Sort Order"
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm((p) => ({ ...p, sort_order: Number(e.target.value) || 0 }))}
            fullWidth
            size="small"
            sx={{ mt: 2 }}
            helperText="Lower values appear first"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !isFormValid}>
            {saving ? <CircularProgress size={20} /> : editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Delete Mashup"
        message="Are you sure you want to delete this mashup? It will be removed from the object's detail view."
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={() => handleDelete(deleteConfirm!)}
        onCancel={() => setDeleteConfirm(null)}
      />
    </Box>
  )
}
